import React, { Fragment, useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import { Link } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import CopyrightIcon from '@mui/icons-material/Copyright';
import DownloadIcon from '@mui/icons-material/Download';
import * as XLSX from 'xlsx';
import '../shared.css';

const StockTransferDetails = () => {
    const { id } = useParams();
    const history = useHistory();
    const [errorMessage, setErrorMessage] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const { isAuthenticated, companyId, branchId, userId, token, logout } = useAuth();

    const [selectedBranch, setSelectedBranch] = useState("");
    const [dropdownBranchOptions, setDropdownBranchOptions] = useState([]);
    const [BranchData, setBranchData] = useState([]);
    const [formData, setFormData] = useState({});
    const [invoicedeatils, setInvoicedeatils] = useState([]);

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
    }, [isAuthenticated, token, logout]);

    useEffect(() => {
        fetchDropdownBranchOptions();
    }, []);

    useEffect(() => {
        if (!id) return;
        fetchData('StockTransferApi', `/${id}`, token)
            .then(response => {
                if (response.responseCode === '1111') {
                    setFormData(response.response_data.StockTransfer);
                    setInvoicedeatils(response.response_data.StockTransferDetails);
                    setFormData(prev => ({ ...prev, UpdatedBy: userId }));
                    setSelectedBranch(response.response_data.StockTransfer.ToBranchId);
                }
            })
            .catch(err => console.error('Error fetching StockTransfer Details:', err));
    }, [id]);

    const fetchDropdownBranchOptions = () => {
        fetchData('StockTransferApi', `/StockTransferBranch/${companyId}/${branchId}`, token)
            .then(r => r.response_data)
            .then(data => { setDropdownBranchOptions(data); setBranchData(data); })
            .catch(err => console.error('Error fetching Branch options:', err));
    };

    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        const d = new Date(dateString);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };

    const handleDownloadExcel = () => {
        const filteredData = invoicedeatils.map((item, index) => ({
            SlNo: index + 1,
            ItemName: item.ItemName,
            Quantity: item.Quantity,
            Rate: item.Rate,
            TotalAmount: item.TotalAmount
        }));
        const selectedBranchData = BranchData.find(branch => branch.Id == selectedBranch);
        const worksheet = XLSX.utils.json_to_sheet(filteredData, { origin: 'A5' });
        XLSX.utils.sheet_add_aoa(worksheet, [['Stock Transfer To ' + (selectedBranchData ? selectedBranchData.Name : '')]], { origin: 'A1' });
        XLSX.utils.sheet_add_aoa(worksheet, [['Stock Transfer No: ' + formData.BillPrefix + '/S/' + formData.BillNumber + '/' + formData.FinYear]], { origin: 'A3' });
        XLSX.utils.sheet_add_aoa(worksheet, [['Date: ' + formatDateForInput(formData.BillDate)]], { origin: 'C3' });
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'StockTransferDetails');
        XLSX.writeFile(workbook, `StockTransferDetails_${id}.xlsx`);
    };

    const titleText = formData.InputRejected
        ? 'Stock Transfer Out — Details (Rejected)'
        : 'Stock Transfer Out — Details';

    return (
        <Fragment>
            <div className="pg-container">

                {/* ── Header ── */}
                <div className="pg-header">
                    <h5 className="pg-title">{titleText}</h5>
                    <div className="pg-header-actions">
                        <div className="pg-action-row">
                            <button className="pg-btn pg-btn-success" onClick={handleDownloadExcel}>
                                <DownloadIcon /> Excel
                            </button>
                            <Link to="/StockTransferIndex" className="pg-btn pg-btn-danger">
                                <CloseIcon /> Back to List
                            </Link>
                        </div>
                    </div>
                </div>

                {errorMessage && <div className="pg-alert pg-alert-error">{errorMessage}</div>}
                {successMessage && <div className="pg-alert pg-alert-success">{successMessage}</div>}

                <div className="pg-card">
                    {/* ── Header fields ── */}
                    <div className="pg-hpanel">
                        <div className="pg-hcell" style={{ flex: '3 1 0' }}>
                            <span className="pg-hcell-label">To Branch</span>
                            <select className="pg-hcell-input" value={selectedBranch} disabled>
                                <option value="">Select Branch</option>
                                {dropdownBranchOptions.map(b => <option key={b.Id} value={b.Id}>{b.Name}</option>)}
                            </select>
                        </div>
                        <div className="pg-hcell" style={{ flex: '1 1 0' }}>
                            <span className="pg-hcell-label">Bill Date</span>
                            <input className="pg-hcell-input pg-hcell-readonly" type="date"
                                value={formatDateForInput(formData.BillDate)} readOnly />
                        </div>
                        <div className="pg-hcell" style={{ flex: '0 0 100px' }}>
                            <span className="pg-hcell-label">Bill No</span>
                            <input className="pg-hcell-input pg-hcell-readonly" type="text"
                                value={formData.BillNumber || ''} readOnly />
                        </div>
                        <div className="pg-hcell" style={{ flex: '0 0 90px' }}>
                            <span className="pg-hcell-label">Total Qty</span>
                            <input className="pg-hcell-input pg-hcell-readonly text-right" type="text"
                                value={formData.TotalQuantity || ''} readOnly />
                        </div>
                    </div>

                    {/* ── Invoice detail table ── */}
                    <div className="pg-invoice-table-wrap" style={{ marginTop: 10 }}>
                        <table className="pg-invoice-table">
                            <thead>
                                <tr>
                                    <th>Item Name</th>
                                    <th className="right">Quantity</th>
                                    <th className="right">Rate</th>
                                    <th className="right">Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {invoicedeatils.length === 0
                                    ? <tr><td colSpan={4} style={{ textAlign: 'center', color: '#94a3b8', padding: 12 }}>No items</td></tr>
                                    : invoicedeatils.map((inv, index) => (
                                        <tr key={index}>
                                            <td>{inv.ItemName}</td>
                                            <td className="right">{inv.Quantity}</td>
                                            <td className="right">{parseFloat(inv.Rate || 0).toFixed(2)}</td>
                                            <td className="right">{parseFloat(inv.TotalAmount || 0).toFixed(2)}</td>
                                        </tr>
                                    ))
                                }
                            </tbody>
                        </table>
                    </div>

                    {/* ── Totals ── */}
                    <div className="pg-totals-grid" style={{ marginTop: 8, gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr 1fr' }}>
                        <div className="pg-field">
                            <label className="pg-label">Remarks</label>
                            <input className="pg-input" type="text" value={formData.Remarks || ''} readOnly placeholder="Remarks" />
                        </div>
                        <div className="pg-field">
                            <label className="pg-label">Amount</label>
                            <input className="pg-input text-right bold" type="text"
                                value={formData.TaxableAmount ? parseFloat(formData.TaxableAmount).toFixed(2) : '0.00'} readOnly />
                        </div>
                        <div className="pg-field">
                            <label className="pg-label">CGST</label>
                            <input className="pg-input text-right bold" type="text"
                                value={formData.CGSTAmount ? parseFloat(formData.CGSTAmount).toFixed(2) : '0.00'} readOnly />
                        </div>
                        <div className="pg-field">
                            <label className="pg-label">SGST</label>
                            <input className="pg-input text-right bold" type="text"
                                value={formData.SGSTAmount ? parseFloat(formData.SGSTAmount).toFixed(2) : '0.00'} readOnly />
                        </div>
                        <div className="pg-field">
                            <label className="pg-label">IGST</label>
                            <input className="pg-input text-right bold" type="text"
                                value={formData.IGSTAmount ? parseFloat(formData.IGSTAmount).toFixed(2) : '0.00'} readOnly />
                        </div>
                        <div className="pg-field">
                            <label className="pg-label">Round Off</label>
                            <input className="pg-input text-right bold" type="text"
                                value={formData.RoundOff || ''} readOnly />
                        </div>
                        <div className="pg-field">
                            <label className="pg-label">Grand Total</label>
                            <input className="pg-input text-right bold grand-total" type="text"
                                value={formData.GrandTotal ? parseFloat(formData.GrandTotal).toFixed(2) : '0.00'} readOnly />
                        </div>
                    </div>
                </div>

                <div className="pg-watermark"><CopyrightIcon style={{ fontSize: 12 }} /><b>EFFTABS SOLUTIONS LLP</b></div>
            </div>
        </Fragment>
    );
};

export default StockTransferDetails;
